import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';

import { LoaderType, ToasterMessage } from '@fcom/common/interfaces';
import { CartStatus, LocationInfoMap, ServiceAvailability, ServiceCatalogCategoryServicesMap } from '@fcom/dapi';
import { SeatMapStatus } from '@fcom/common/interfaces/seat-map.interface';
import { Category, FinnairBoundItem, FinnairCart, FinnairPassengerItem, FinnairPrice } from '@fcom/dapi/api/models';
import { finShare, safeMap } from '@fcom/rx';
import { isNotEmpty } from '@fcom/core/utils';
import {
  seatMapForFlight,
  seatMapForFlightStatus,
  serviceCatalogCampaignIdsForFragment,
  serviceCategoriesMap,
} from '@fcom/common-booking/store/selectors';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { getBoundsAndTravelersWhichHavePetcIncompatibleSeats } from '@fcom/common-booking/modules/ancillaries/utils';
import { LanguageService } from '@fcom/ui-translate';
import { FlightWithStreams } from '@fcom/common-booking/interfaces';
import { BookingAppState, PaxDetailsState } from '@fcom/common/interfaces/booking';
import { profileType } from '@fcom/core/selectors';
import { ProfileType } from '@fcom/core-api/login';

import { AppConstants } from '../../../app-constants';
import { cartStatus, paxDetails } from '../../../store/selectors';

@Component({
  selector: 'fin-ancillaries',
  templateUrl: './ancillaries.component.html',
  styleUrls: ['./ancillaries.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AncillariesComponent implements OnInit {
  @Input()
  cartData$: Observable<FinnairCart>;
  @Input()
  cartStatus$: Observable<CartStatus>;
  @Input()
  serviceAvailability$: Observable<ServiceAvailability>;
  @Input()
  servicesReady$: Observable<boolean>;

  @Output()
  ready: EventEmitter<boolean> = new EventEmitter<boolean>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output()
  message: EventEmitter<ToasterMessage> = new EventEmitter<ToasterMessage>();

  public readonly C: typeof AppConstants = AppConstants;
  public readonly LoaderType = LoaderType;

  dataReady$: Observable<boolean>;
  bounds$: Observable<FinnairBoundItem[]>;
  allFlights$: Observable<FlightWithStreams[]>;
  passengers$: Observable<FinnairPassengerItem[]>;
  cartTotalPrice$: Observable<FinnairPrice>;
  priceUpdating$: Observable<boolean>;
  hasTravelerInCart$: Observable<boolean>;
  servicesMap$: Observable<ServiceCatalogCategoryServicesMap>;
  locations$: Observable<LocationInfoMap>;
  passengersWithPetcIncompatibleSeats$: Observable<{ [boundId: string]: string[] }>;
  paxDetails$: Observable<PaxDetailsState>;

  constructor(
    private store$: Store<BookingAppState>,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    const profileType$ = this.store$.pipe(profileType());
    this.dataReady$ = combineLatest([this.cartData$, profileType$]).pipe(
      safeMap(
        ([data, userProfileType]) =>
          Boolean(data.bounds) && (userProfileType !== ProfileType.CORPORATE || Boolean(data.passengers?.[0].lastName))
      ),
      startWith(false),
      distinctUntilChanged()
    );
    this.locations$ = this.cartData$.pipe(map((cd) => cd.locations));
    this.bounds$ = this.cartData$.pipe(safeMap((data: FinnairCart) => data.bounds));
    this.paxDetails$ = this.store$.pipe(paxDetails());
    this.servicesMap$ = this.store$.pipe(serviceCategoriesMap());
    this.allFlights$ = this.cartData$.pipe(
      filter((cd) => !!cd.bounds),
      map((cd) =>
        cd.bounds
          .reduce((allFlights, bound) => allFlights.concat(bound.itinerary.filter(isFlight)), [])
          .map((flight) => {
            const seatMapStatus$: Observable<SeatMapStatus> = combineLatest([
              this.store$.pipe(seatMapForFlightStatus(flight.id)),
              this.store$.pipe(
                cartStatus(),
                map((s) =>
                  cd.otherInformation?.useSeatmapPrefetch
                    ? [
                        CartStatus.INITIAL,
                        CartStatus.PENDING_CREATION,
                        CartStatus.CREATION_ERROR,
                        CartStatus.LOAD_ERROR,
                      ].includes(s) === false
                    : s === CartStatus.OK
                )
              ),
            ]).pipe(
              map(([seatMapStatus, cart]) => (!cart ? SeatMapStatus.PENDING : seatMapStatus)),
              distinctUntilChanged()
            );
            return {
              ...flight,
              seatMap$: this.store$.pipe(seatMapForFlight(flight.id)),
              seatMapStatus$,
              seatSubViewTopNotificationTranslation$: this.store$.pipe(
                serviceCatalogCampaignIdsForFragment(flight.id),
                filter((campaigns) => campaigns && campaigns[Category.SEAT] && campaigns[Category.SEAT].length > 0),
                map((campaigns) => `campaign.notifications.seatSubView.${campaigns[Category.SEAT][0]}`),
                switchMap((translationKey) => this.languageService.translate(translationKey))
              ),
            };
          })
      ),
      finShare()
    );

    const paxCartData$ = this.cartData$.pipe(
      safeMap((data: FinnairCart) => data.passengers),
      finShare()
    );
    this.passengers$ = combineLatest([paxCartData$.pipe(), this.paxDetails$]).pipe(
      map(([cartPassengers, passengerDetails]) => {
        return cartPassengers.map((pax) => ({
          ...pax,
          firstName: pax.firstName || passengerDetails.passengers.find((p) => p.travelerId === pax.id)?.firstName,
          lastName: pax.lastName || passengerDetails.passengers.find((p) => p.travelerId === pax.id)?.lastName,
        }));
      }),
      finShare()
    );

    this.cartTotalPrice$ = this.cartData$.pipe(
      safeMap((data: FinnairCart) => data.prices),
      safeMap((prices) => prices.unpaid.total.total),
      distinctUntilChanged(),
      finShare()
    );
    this.hasTravelerInCart$ = paxCartData$.pipe(
      map((travelers) => isNotEmpty(travelers) && travelers.every((t) => !!t.firstName))
    );
    this.priceUpdating$ = combineLatest([
      this.cartStatus$.pipe(map((status: CartStatus) => status !== CartStatus.OK)),
      this.hasTravelerInCart$,
    ]).pipe(map(([statusNotOk, travelersOk]: [boolean, boolean]) => statusNotOk || !travelersOk));

    this.passengersWithPetcIncompatibleSeats$ = this.cartData$.pipe(
      map((booking) => getBoundsAndTravelersWhichHavePetcIncompatibleSeats(booking, 'unpaid')),
      finShare()
    );
  }
}
